import {MISC_KEYS} from "@/api/miscApi";
import type {TokenSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import useRegistrationStore from "@/store/registration";
import useTokenStore from "@/store/token";
import type {TGoogleRegistrationPayload} from "@/store/types";
import type {TError} from "@/utils/errorHandlers";
import {identifyHubSpotVisitor} from "@/utils/hubspot";
import {useMutation} from "@tanstack/react-query";
import type {AxiosAuthRefreshRequestConfig} from "axios-auth-refresh";
import Cookies from "js-cookie";

export const useLogin = (onError?: (error: TError) => void) => {
  const setAccessToken = useTokenStore(state => state.setAccessToken);
  const setRefreshToken = useTokenStore(state => state.setRefreshToken);
  const reset = useRegistrationStore(state => state.reset);

  const {
    mutate: login,
    mutateAsync: asyncLogin,
    isPending,
  } = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: MISC_KEYS.mToken,
    mutationFn: (data: {email: string; password: string}) =>
      client.post<TokenSerializer>(MISC_KEYS.mToken[0], data, {skipAuthRefresh: true} as AxiosAuthRefreshRequestConfig),
    onSuccess: response => {
      Cookies.set("isLoggedIn", "true", {expires: 365});
      sessionStorage.setItem("justLoggedIn", "true");
      setAccessToken(response.data.access);
      setRefreshToken(response.data.refresh);
      reset();
    },
    onError,
  });

  return {login, asyncLogin, isPending};
};

export const useLoginWithGoogle = () => {
  const setAccessToken = useTokenStore(state => state.setAccessToken);
  const setRefreshToken = useTokenStore(state => state.setRefreshToken);
  const reset = useRegistrationStore(state => state.reset);

  return useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: MISC_KEYS.mGoogleLogin,
    mutationFn: ({access_token, payload}: {access_token: string; payload?: TGoogleRegistrationPayload}) =>
      client.post<TokenSerializer & {email?: string}>(
        MISC_KEYS.mGoogleLogin[0],
        {access_token, ...payload},
        {
          headers: {"Content-Type": "multipart/form-data"},
        },
      ),
    onSuccess: response => {
      identifyHubSpotVisitor(response.data.email || useRegistrationStore.getState().email);
      Cookies.set("isLoggedIn", "true", {expires: 365});
      sessionStorage.setItem("justLoggedIn", "true");
      setAccessToken(response.data.access);
      setRefreshToken(response.data.refresh);
      reset();
    },
  });
};
